import React, { useState } from 'react';
import { set } from 'dot-prop-immutable';
import { toolFormAttr, toolCollapseAttr, getLocale } from '@utils/util';
import { SimpleForm as Form, Collapse, Switch, Input, Select, Divider, Tooltip } from '@whalecloud/fdx';
import CustomInput from '@whalecloud/page-builder-base-components/components/CustomInput';
import CommonItemSettings from '@whalecloud/page-builder-base-components/components/ElementSettings/CommonItemSettings';

const { Panel } = Collapse;

const acceptOpts = [
  { label: 'Any', value: 'image/*' },
  { label: 'JPG', value: 'image/jpeg' },
  { label: 'PNG', value: 'image/png' },
  { label: 'GIF', value: 'image/gif' },
  { label: 'TIFF', value: 'image/tiff' },
  { label: 'WBMP', value: 'image/vnd.wap.wbmp' },
];

const valueKey = 'data.formItemSettings';

const UploadSettings = (props: any) => {
  const messages = getLocale();
  
  const { updateElement, element } = props;
  const { data: { formItemSettings = {} } = {} } = element;

  const {
    accept = 'image/*',
    action,
    download,
    directory = false,
    withCredentials = false,
    multiple = false,
    width,
    height,
    acceptOptions,
    successMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_SUCCESS_DEFAULT,
    errorMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_ERROR_DEFAULT,
    failMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_FAILD_DEFAULT,
    fileIdName,
  } = formItemSettings;
  const perWidth = width && Math.round((width / document.body.clientWidth) * 100);
  const perHeight = height && Math.round(((height + 38.6) / document.body.clientHeight) * 100);
  const initialValues = {
    accept,
    action,
    download,
    directory,
    withCredentials,
    multiple,
    width: perWidth || 50,
    height: perHeight || 50,
    successMessage,
    errorMessage,
    failMessage,
    fileIdName,
  };

  const [name, setName] = useState('');
  const [optionArr, setOptionArr] = useState(acceptOptions || acceptOpts);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const onNameChange = (event: any) => {
    setName(event.target.value);
    setTooltipVisible(false);
  };

  const addItem = () => {
    if (name) {
      const optionObj = {
        label: name,
        value: name,
      };
      const newArr = [...optionArr, optionObj];
      setOptionArr(newArr);
      const newElement = set(element, `${valueKey}.acceptOptions`, newArr);
      updateElement({
        element: newElement,
      });
    } else {
      setTooltipVisible(true);
    }
  };

  const onFormChange = (obj: Object) => {
    Object.keys(obj).forEach((key: string) => {
      const propName = `${valueKey}.${key}`;
      let value = obj[key];
      if (key === 'width') {
        value = (value / 100) * document.body.clientWidth;
      }
      if (key === 'height') {
        value = (value / 100) * document.body.clientHeight - 38.6;
      }
      const newElement = set(element, propName, value);
      updateElement({
        element: newElement,
      });
    });
  };

  return (
    <Collapse {...toolCollapseAttr} defaultActiveKey={['1', '2']}>
      <Panel header={messages.PAGEBUIDER_SETTINGS} key="1">
        <CommonItemSettings defaultValueHide allowClearHide placeholderHide />
        <Form {...toolFormAttr} initialValues={initialValues} onValuesChange={onFormChange}>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_ACCEPT} name="accept">
            <Select
              style={{ width: '100%' }}
              placeholder={messages.PAGEBUIDER_SETTINGS_PLS_SELECT_FILE_TYPE}
              dropdownRender={menu => (
                <div>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8, alignItems: 'center' }}>
                    <Tooltip title={messages.PAGEBUIDER_SETTINGS_PLS_INPUT_ACCEPT_TYPE} visible={tooltipVisible}>
                      <Input style={{ flex: 'auto' }} value={name} onChange={onNameChange} />
                    </Tooltip>
                    <a
                      style={{ flex: 'none', padding: '8px', display: 'block', cursor: 'pointer' }}
                      onClick={addItem}
                    >
                      {messages.PAGEBUIDER_SETTINGS_ADD_ITEM}
                    </a>
                  </div>
                </div>
              )}
              options={optionArr}
            />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_WITH_CREDENTIALS} name="withCredentials" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_DIRECTORY} name="directory" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_MULTIPLE} name="multiple" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_MODAL_WIDTH} name="width">
            <CustomInput
              inputType="inputNumber"
              inputProps={{
                defaultValue: 100,
                min: 0,
                max: 100,
                formatter: value => `${value}%`,
                parser: value => value.replace('%', ''),
              }}
            />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_MODAL_HEIGHT} name="height">
            <CustomInput
              inputType="inputNumber"
              inputProps={{
                defaultValue: 100,
                min: 0,
                max: 100,
                formatter: value => `${value}%`,
                parser: value => value.replace('%', ''),
              }}
            />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_SUCCESS} name="successMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_ERROR} name="errorMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_FAILD} name="failMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_ID_NAME} name="fileIdName">
            <CustomInput />
          </Form.Item>
        </Form>
      </Panel>
      <Panel header="URL Settings" key="2">
        <Form {...toolFormAttr} initialValues={initialValues} onValuesChange={onFormChange}>
          <Form.Item label="Upload URL" name="action">
            <CustomInput />
          </Form.Item>
          <Form.Item label="Download URL" name="download">
            <CustomInput />
          </Form.Item>
        </Form>
      </Panel>
    </Collapse>
  );
};

export default UploadSettings;
